import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { computed } from 'mobx'

/**
 * Summarizes the selected workspace's contract on the firm dashboard.
 */
export class FirmDashboardContractViewModel {
  constructor(private readonly _getWorkspace: () => Workspace) {}

  /**
   * The workspace whose contract is shown.
   */
  @computed
  get currentWorkspace() {
    return this._getWorkspace()
  }

  /**
   * Whether the contract config is enabled within the workspace.
   */
  @computed
  get isContractConfigEnabled() {
    return this.currentWorkspace.config.contract.enabled
  }

  /**
   * Total amount ($USD) of the contract.
   */
  @computed
  get contractTotal() {
    return this.currentWorkspace.config.contract.amount
  }

  /**
   * Total fees ($USD) charged under the contract.
   */
  @computed
  get feeAmount() {
    return this.vmFees + this.setupFees
  }

  /**
   * Total VM fees ($USD).
   */
  @computed
  get vmFees() {
    return this.vmFeesAmountPerUnit * this.vmFeesQuantity
  }

  /**
   * Number of VM fee units billed.
   */
  @computed
  get vmFeesQuantity() {
    return this.currentWorkspace.config.contract.vmFees.quantity
  }

  /**
   * VM fee ($USD) per unit.
   */
  @computed
  get vmFeesAmountPerUnit() {
    return this.currentWorkspace.config.contract.vmFees.amountPerUnit
  }

  /**
   * One-off setup fee ($USD).
   */
  @computed
  get setupFees() {
    return this.currentWorkspace.config.contract.setupFeeAmount
  }

  /**
   * Start date of the contract.
   */
  @computed
  get contractStartDate() {
    return this.isContractConfigEnabled
      ? this.currentWorkspace.config.contract.startDate
      : null
  }

  /**
   * End date of the contract.
   */
  @computed
  get contractEndDate() {
    return this.isContractConfigEnabled
      ? this.currentWorkspace.config.contract.endDate
      : null
  }
}
